import { notification, Tag } from 'antd'
import EntityListContainer from './EntityListContainer'
import EntityDetail from './EntityDetail'
import { setSpaces } from './AclDataProvider/actions'
import { Detail, Domains, Functions, Roles, Users } from './DetailPanels'
import { Database } from 'react-feather'
import ClearSpace from './ClearSpacePartial'
import WeaverService from 'Services/WeaverService'
import ACLService from 'Services/ACL_Service'
import { clearSpace } from 'Services/WeaverUtils'
import cuid from 'cuid'

getUsers = (space) ->
  acl = await space.getACL()

  Promise.filter(await Weaver.User.list(), (user) ->
    getPermissions(acl, user)
  )

getPermissions = (acl, user) ->
  roles = await user.getRoles()

  user.hasRoleReadAccess = roles.map((role) ->
    acl.getRoleReadAccess(role)
  ).some((canRead)-> canRead)

  user.hasRoleWriteAccess = roles.map((role) ->
    acl.getRoleWriteAccess(role)
  ).some((canWrite)-> canWrite)

  user.hasWriteAccess = acl.getUserWriteAccess(user) or user.hasRoleWriteAccess
  user.cannotChangePermissions = user.hasRoleWriteAccess
  user.cannotRevoke = user.hasRoleReadAccess

  user.hasRoleReadAccess or acl.getUserReadAccess(user)

addUser = (space, user) ->
  acl = await space.getACL()
  acl.setUserReadAccess(user, true)
  await acl.save()
  await getPermissions(acl, user)
  user

removeUser = (space, user) ->
  acl = await space.getACL()
  acl.setUserReadAccess(user, false)
  acl.setUserWriteAccess(user, false)
  acl.save()

setUserWriteAccess = (space, user, canWrite) ->
  acl = await space.getACL()
  acl.setUserWriteAccess(user, canWrite)
  acl.save()

getDomains = (space) ->
  acl = await space.getACL()
  _.filter(await Weaver.Domain.list(), (domain) ->
    domain.hasWriteAccess = acl.getRoleWriteAccess(domain)
    acl.getRoleReadAccess(domain)
  )

getRoles = (space) ->
  acl = await space.getACL()
  _.filter(await Weaver.Role.list(), (role) ->
    role.hasWriteAccess = acl.getRoleWriteAccess(role)
    role.hasReadAccess = acl.getRoleReadAccess(role)
    not role._isDomain and role.hasReadAccess
  )

addRole = (space, role) ->
  acl = await space.getACL()
  acl.setRoleReadAccess(role, true)
  await acl.save()
  role

removeRole = (space, role) ->
  acl = await space.getACL()
  acl.setRoleReadAccess(role, false)
  acl.setRoleWriteAccess(role, false)
  acl.save()

setRoleWriteAccess = (space, role, canWrite) ->
  acl = await space.getACL()
  acl.setRoleWriteAccess(role, canWrite)
  acl.save()

getFunctions = (space) ->
  _.sortBy(await ACLService.loadFunctionAcls(), 'id')

generateFields = (action) ->
  cloneIdField = { field: 'cloneId', label: 'Clone id', initialValue: cuid() }
  nameField = { field: '_name', label: 'Name' }
  idField = { field: '_id', label: 'Space id', editable: false }
  publicField =  {
    field: 'public',
    info:
      <span>This will set public access to the space. This will allow <b>everyone - registered and unregistered</b> to view all data in this space.</span>
    onSave: (space, value) ->
      acl = await space.getACL()
      acl.setPublicReadAccess(value)
      acl.save()
  }
  if action is 'clone'
    [ nameField, idField, cloneIdField, publicField ]
  else
    [ nameField, idField, publicField ]

export DetailView = EntityDetail({
  icon: 'database'
  title: (space) -> space.name()
  getItem: (id, action) ->
    if id is 'new'
      space = new Weaver.Project('new-space')
      space.isCreating = true
      space.public = false
      space
    else if action is 'clone'
      space = await Weaver.Project.load(id)
      space.public = (await space.getACL()).getPublicReadAccess()
      space
    else
      space = await Weaver.Project.load(id)
      space.public = (await space.getACL()).getPublicReadAccess()
      space
  tabs: [{
    title: 'Space Detail'
    Component: Detail({
      displayName: (item) -> item.name()
      fields: generateFields
      onDelete: ({ item, history }) ->
        await item.destroy()
        history.push('/acl/spaces')
      afterCreate: (space) ->
        user = Weaver.getInstance().currentUser()
        acl = await space.getACL()
        acl.setUserReadAccess(user, true)
        acl.setUserWriteAccess(user, true)
        acl.save()
      editActions: [
        ({ item }) -> <ClearSpace space={item} />
      ]
    })
  }, {
    title: 'Domains'
    disabled: (space) -> space.isCreating
    Component: Domains({ getDomains, addDomain: addRole, removeDomain: removeRole })
  }, {
    title: 'User Access'
    disabled: (space) -> space.isCreating
    Component: Users({ getUsers, setWriteAccess: setUserWriteAccess })
  }, {
    title: 'Group Access'
    disabled: (space) -> space.isCreating
    Component: Roles({ getRoles, addRole, removeRole, setWriteAccess: setRoleWriteAccess })
  }]
})

export ListView = EntityListContainer({
  title: 'All spaces'
  singular: 'space'
  singularRoute: 'spaces'
  Icon: Database
  init: ->
    spcs = _.sortBy(await Weaver.Project.list(), (project) -> project.name())
    spaces = await Promise.all(spcs.map((p) ->
      p.public = (await p.getACL()).getPublicReadAccess()
      try
        { status } = await p.isFrozen()
        p.frozen = status
      catch
        p.frozen = false
      p
    ))
    setSpaces(spaces)
  reducerKey: 'spaces'
  filterer: (str) -> (item) -> item.name().toLowerCase().indexOf(str.toLowerCase()) isnt -1
  itemProps: {
    title: (item) -> item.name()
    titleExtra: (item) ->
      <>
        {
          if item.frozen
            <Tag color="blue">Frozen</Tag>
        } {
          if item.public
            <Tag color="green">Public</Tag>
        }
      </>
    key: (item) -> item.id()
  }
  actions: [{
    content: -> 'Clone',
    fn: (item, history) -> history.push("/acl/spaces/#{item.id()}/clone")
    accessFn: (item) -> ACLService.checkHasWriteAccess(item)
  }, {
    content: (item) -> item.frozen && 'Unfreeze' || 'Freeze'
    fn: (item) ->
      try
        hasWriteAccess = await ACLService.checkHasWriteAccess(item)
        if item.frozen
          fn = 'unfreeze'
        else
          fn = 'freeze'
        await item[fn]() if hasWriteAccess
      catch e
        notification.error({
          message: "Cannot freeze project '#{item.name()}'"
          description: e.message
        })
    accessFn: (item) -> ACLService.checkHasWriteAccess(item)
  }, {
    content: -> 'Wipe',
    requiresConfirmation: true
    danger: true
    fn: clearSpace
    accessFn: (item) ->
      hasWriteAccess = await ACLService.checkHasWriteAccess(item)
      isFrozen = item.frozen
      !isFrozen and hasWriteAccess
  }, {
    content: -> 'Delete'
    requiresConfirmation: true
    danger: true
    fn: (item) -> item.destroy()
    accessFn: (item) -> ACLService.checkHasWriteAccess(item)
  }]
})
